<script setup lang="ts">
    import { computed, watch } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import EchoButton from '@/js/components/EchoButton.vue';
    import FormGroup from '@/js/components/FormGroup.vue';
    import SelectInput from '@/js/components/SelectInput.vue';
    import EchoInfoBox from '@/js/components/EchoInfoBox.vue';
    import TextInput, { TextInputWidth } from '@/js/components/TextInput.vue';
    import RadioOption from '@/js/components/RadioOption.vue';
    import {
        allowsQueueAndSkillOverrides,
        isRetentionRuleImplemented,
        type RetentionDataType,
        type RetentionQueueOption,
        type RetentionSkillOption,
    } from '@/js/services/api/backOffice/retentionSettingsApi';

    export type RetentionScope = 'all' | 'queue' | 'skill';

    // Shared by every place a retention setting can be created or edited (top-level create
    // panel, inline row edit).
    const props = defineProps<{
        idPrefix: string;
        dataTypeOptions: RetentionDataType[];
        dataTypeLabel: (dataType: RetentionDataType) => string;
        dataTypeDescription: (dataType: RetentionDataType) => string;
        queueOptions: RetentionQueueOption[];
        skillOptions: RetentionSkillOption[];
        takenQueueIds: Set<string>;
        takenSkillIds: Set<string>;
        hasExistingBase: boolean;
        isPending: boolean;
        showDelete: boolean;
        isDeleting: boolean;
    }>();

    const emit = defineEmits<{
        submit: [];
        cancel: [];
        delete: [];
    }>();

    const dataType = defineModel<RetentionDataType>('dataType', { required: true });
    const retentionDays = defineModel<number | null>('retentionDays', { required: true });
    const scope = defineModel<RetentionScope>('scope', { required: true });
    const queueId = defineModel<string | null>('queueId', { required: true });
    const skillId = defineModel<string | null>('skillId', { required: true });

    // TextInput/SelectInput models don't accept null - map it to the empty/undefined value
    // their own model types use, at this one boundary, so the rest of the component (and the
    // page's payload building) can keep treating "not set" as null throughout.
    const retentionDaysInput = computed<number | undefined>({
        get: () => retentionDays.value ?? undefined,
        set: (value) => {
            retentionDays.value = value ?? null;
        },
    });
    const queueIdInput = computed<string>({
        get: () => queueId.value ?? '',
        set: (value) => {
            queueId.value = value || null;
        },
    });
    const skillIdInput = computed<string>({
        get: () => skillId.value ?? '',
        set: (value) => {
            skillId.value = value || null;
        },
    });

    const allowsOverrides = computed(() => allowsQueueAndSkillOverrides(dataType.value));
    watch(
        allowsOverrides,
        (allowed) => {
            if (!allowed) scope.value = 'all';
        },
        { immediate: true }
    );

    const isDuplicateScope = computed(() => {
        if (scope.value === 'all') return props.hasExistingBase;
        if (scope.value === 'queue')
            return !!queueId.value && props.takenQueueIds.has(queueId.value);
        if (scope.value === 'skill')
            return !!skillId.value && props.takenSkillIds.has(skillId.value);
        return false;
    });

    const canSubmit = computed(
        () =>
            !!retentionDays.value &&
            retentionDays.value > 0 &&
            (scope.value !== 'queue' || !!queueId.value) &&
            (scope.value !== 'skill' || !!skillId.value) &&
            !isDuplicateScope.value
    );

    const onSubmit = () => {
        if (!canSubmit.value) return;
        emit('submit');
    };
</script>
<template>
    <form class="flex flex-col gap-6" @submit.prevent="onSubmit">
        <FormGroup>
            <div class="flex flex-col gap-1">
                <label :for="`${idPrefix}-data-type`" class="text-overline">
                    {{ __('back-office-retention-data-type') }}
                </label>
                <SelectInput :id="`${idPrefix}-data-type`" v-model="dataType">
                    <option
                        v-for="dt in dataTypeOptions"
                        :key="dt"
                        :value="dt"
                        :disabled="!isRetentionRuleImplemented(dt)"
                    >
                        {{ dataTypeLabel(dt) }}
                        <template v-if="!isRetentionRuleImplemented(dt)">
                            ({{ __('back-office-retention-not-implemented') }})
                        </template>
                    </option>
                </SelectInput>
                <EchoInfoBox class="mt-1">
                    {{ dataTypeDescription(dataType) }}
                </EchoInfoBox>
            </div>

            <div class="flex flex-col gap-1">
                <label :for="`${idPrefix}-retention-days`" class="text-overline">
                    {{ __('back-office-retention-days') }}
                </label>
                <TextInput
                    :id="`${idPrefix}-retention-days`"
                    v-model.number="retentionDaysInput"
                    type="number"
                    min="1"
                    :width="TextInputWidth.Small"
                />
            </div>

            <div v-if="allowsOverrides" class="flex flex-col gap-2">
                <span class="text-overline">{{ __('back-office-retention-scope') }}</span>
                <div class="flex gap-6">
                    <RadioOption
                        :id="`${idPrefix}-scope-all`"
                        v-model="scope"
                        :name="`${idPrefix}-scope`"
                        value="all"
                        :label="__('back-office-retention-scope-all')"
                    />
                    <RadioOption
                        :id="`${idPrefix}-scope-queue`"
                        v-model="scope"
                        :name="`${idPrefix}-scope`"
                        value="queue"
                        :label="__('back-office-retention-scope-queue')"
                    />
                    <RadioOption
                        :id="`${idPrefix}-scope-skill`"
                        v-model="scope"
                        :name="`${idPrefix}-scope`"
                        value="skill"
                        :label="__('back-office-retention-scope-skill')"
                    />
                </div>
            </div>

            <div v-if="scope === 'queue'" class="flex flex-col gap-1">
                <label :for="`${idPrefix}-queue`" class="text-overline">
                    {{ __('back-office-retention-queue') }}
                </label>
                <SelectInput :id="`${idPrefix}-queue`" v-model="queueIdInput">
                    <option value="" disabled>
                        {{ __('back-office-retention-select-queue') }}
                    </option>
                    <option v-for="q in queueOptions" :key="q.id" :value="q.id">
                        {{ q.name }}
                    </option>
                </SelectInput>
            </div>

            <div v-if="scope === 'skill'" class="flex flex-col gap-1">
                <label :for="`${idPrefix}-skill`" class="text-overline">
                    {{ __('back-office-retention-skill') }}
                </label>
                <SelectInput :id="`${idPrefix}-skill`" v-model="skillIdInput">
                    <option value="" disabled>
                        {{ __('back-office-retention-select-skill') }}
                    </option>
                    <option v-for="s in skillOptions" :key="s.id" :value="s.id">
                        {{ s.name }}
                    </option>
                </SelectInput>
            </div>

            <p v-if="isDuplicateScope" class="text-body text-error">
                {{
                    __(
                        scope === 'all'
                            ? 'back-office-retention-duplicate-base'
                            : 'back-office-retention-duplicate-scope'
                    )
                }}
            </p>
        </FormGroup>

        <div class="flex justify-between">
            <EchoButton
                v-if="showDelete"
                type="button"
                variant="secondary"
                :loading="isDeleting"
                @click="emit('delete')"
            >
                {{ __('delete') }}
            </EchoButton>
            <div class="flex gap-3">
                <EchoButton
                    type="submit"
                    variant="primary"
                    :disabled="!canSubmit"
                    :loading="isPending"
                >
                    {{ __('save') }}
                </EchoButton>
                <EchoButton type="button" variant="secondary" @click="emit('cancel')">
                    {{ __('cancel') }}
                </EchoButton>
            </div>
        </div>
    </form>
</template>
